import {Component, EventEmitter, Inject, inject, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef} from '@angular/material/dialog';
import {INewMissionDialog} from './new-mission-dialog.interface';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators,} from '@angular/forms';
import {
  ConfirmIconButtonComponent,
  DateElementComponent,
  DialogHeaderComponent,
  IDateFormElement,
  ISelectFormElement,
  ITextFormElement,
  SelectElementComponent,
  TextElementComponent,
} from 'frontier/browserkit';
import {Subscription} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {DateTime} from 'luxon';
import {cartNumberValidatorFactory} from './cart-number.validator';
import {INewMissionForm} from './new-mission-form.interface';
import {CartListService} from 'src/app/api/generated';
import {MatButton} from "@angular/material/button";
import {ControlStore, EControlActions, IControlDataChanged} from "frontier";
import AppControlGuids from "../../../../../api/ControlGuids";

@Component({
  selector: 'app-new-mission-dialog',
  templateUrl: './new-mission-dialog.component.html',
  styleUrls: ['./new-mission-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [DialogHeaderComponent, MatDialogContent, TextElementComponent, DateElementComponent, SelectElementComponent,
    MatDialogActions, ConfirmIconButtonComponent, MatButton]
})
export class NewMissionDialogComponent implements OnInit, OnDestroy {
  createAdditional: EventEmitter<INewMissionDialog> = new EventEmitter<INewMissionDialog>();

  private _cartListService: CartListService = inject(CartListService);
  protected readonly controlStore: ControlStore = inject(ControlStore);
  form: FormGroup<INewMissionForm> = this.fb.group({
    missionNumber: this.fb.control(String(DateTime.local().year) + '|', [
      Validators.required,
      Validators.pattern(/^[0-9]{4}\|[a-zA-Z0-9][0-9]{1,9}$/),
    ], [
      cartNumberValidatorFactory(this._cartListService, this.data.instanceId)
    ]),
    date: this.fb.control(null, [
      Validators.required,
      this.dateRangeValidator(),
    ]),
    businessAreaSignature: this.fb.control(null, [Validators.required]),
    ambulanceStationSignature: this.fb.control(null),
  });
  missionNumberData: ITextFormElement = {
    inputType: 'text',
    label: 'Einsatznummer',
  };
  dateData: IDateFormElement = {
    label: 'Datum',
    insertDelimiter: false,
  };
  selectBusinessAreaData: ISelectFormElement = {
    label: 'Geschäftsfeld',
    options: [],
    createNewEnabled: false,
    deleteElementEnabled: false,
    editElementEnabled: false,
    emptyOptionAdded: false,
  };
  selectAmbulanceStationData: ISelectFormElement = {
    label: 'Rettungswache',
    options: [],
    createNewEnabled: false,
    deleteElementEnabled: false,
    editElementEnabled: false,
    emptyOptionAdded: false,
  }
  customMissionNumberValidatorErrorMessages: Map<string, string> = new Map([
    ['invalidCartNumber', 'Die Einsatznummer existiert schon.'],
    ['failedCartNumberCheck', 'Die Einsatznummer konnte nicht überprüft werden.'],
  ]);
  customDateValidatorErrorMessages: Map<string, string> = new Map([
    ['dateOutOfRange', 'Das Datum muss innerhalb der letzten 5 Jahre oder maximal 1 Jahr in der Zukunft liegen.'],
    ['yearMismatch', 'Der Anfang der Einsatznummer muss mit dem Jahr des Datums übereinstimmen.'],
  ])


  subs = new Subscription();

  constructor(@Inject(MAT_DIALOG_DATA) public data: INewMissionDialog,
              public dialogRef: MatDialogRef<NewMissionDialogComponent>,
              private fb: FormBuilder) {
    this.subs.add(
      this.form.controls.missionNumber.valueChanges.pipe(
        tap(value => {
          let numericValue = value.replace(/[^a-zA-Z0-9]/g, '');

          // Format the input according to the specified pattern
          if (numericValue.length >= 4) {
            numericValue = numericValue.substring(0, 4) + '|' + numericValue.substring(4);
          }
          // Only patch when the format actually changed — patchValue reruns the async
          // validator even with emitEvent: false, doubling the validation http call.
          if (numericValue !== value) {
            this.form.controls.missionNumber.patchValue(numericValue, {emitEvent: false});
          }
        })
      ).subscribe()
    );

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged): boolean => {
          return new Map([
            [AppControlGuids.TCartList, new Set([EControlActions.create])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap((): void => this.form.get('missionNumber')?.updateValueAndValidity()),
      ).subscribe(),
    );

    if (data.edit) {
      this.form.controls.date.removeValidators(Validators.required);
      this.form.controls.businessAreaSignature.removeValidators(Validators.required);
      this.form.controls.date.updateValueAndValidity();
      this.form.controls.businessAreaSignature.updateValueAndValidity();
    }

    this.selectBusinessAreaData.options = data.businessAreas || this.selectBusinessAreaData.options;
    this.selectAmbulanceStationData.options = data.ambulanceStations || this.selectAmbulanceStationData.options;
    if (data.missionNumber) {
      this.form.patchValue({
        missionNumber: data.missionNumber
      });
    }
  }

  ngOnInit(): void {
    this.form.get('missionNumber')?.valueChanges.subscribe(() => {
      this.form.get('date')?.updateValueAndValidity();
    });
  }


  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onBackspace(evt: Event) {
    const value = this.form.value.missionNumber;
    if (value.length !== 5) return;
    const newValue = value.substring(0, value.length - 1);
    this.form.controls.missionNumber.patchValue(newValue, {emitEvent: false});
    evt.stopPropagation();
    evt.preventDefault();
  }

  private dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const selectedDate: DateTime<true> | DateTime<false> = DateTime.fromISO(control.value);
      const now: DateTime<true> = DateTime.local();

      const minDate: DateTime<true> = now.minus({years: 5});
      const maxDate: DateTime<true> = now.plus({years: 1});

      let validationErrors: ValidationErrors = {};
      if (selectedDate < minDate || selectedDate > maxDate) {
        validationErrors['dateOutOfRange'] = true;
      }

      const missionNumber: string = this.form.value.missionNumber;

      if (missionNumber && selectedDate) {
        const missionYear: string = missionNumber.split('|')[0];
        const selectedYear: string = selectedDate.year.toString();

        if (missionYear != selectedYear) {
          validationErrors['yearMismatch'] = true;
        }
      }

      return validationErrors;
    };
  }

  onCreateAdditional(): void {
    this.createAdditional.emit(this.form.value as INewMissionDialog);
    this.form.controls.missionNumber.patchValue(String(DateTime.local().year) + '|');
    this.form.controls.missionNumber.markAsUntouched();
  }
}
